import React, { useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Image, Switch } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import moment from 'moment-timezone';
import { cloneDeep } from 'lodash';
import Icon from 'react-native-vector-icons/dist/Feather';
import imageCompression from 'browser-image-compression';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import { DatePickerModal, TimePickerModal } from 'react-native-paper-dates';

// Custom Components
import { CityPickerModal, NavBar, TextBox } from '../../components';
import LoadingView from '../../components/component_loading_view';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';
import { getFileName } from '../../utils/util_files';

// APIs
import { createNewTopic, updateTopic } from '../../modules/api_forum';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

export function ChatNewTopic() {
    const { onNewTopicCreated, topicData = null } = useRoute()?.params || {};

    const PHOTO_SIZE = 120;
    const MAX_PHOTO_COUNT = 5;

    // region Refs
    const cityPicker = useRef();
    const photoPicker = useRef(null);
    const txtTitle = useRef(null);
    const txtDescription = useRef(null);
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [title, setTitle] = useState(topicData?.title || '');
    const [description, setDescription] = useState(topicData?.description || '');
    const [postingCities, setPostingCities] = useState((topicData?.['serving_city'] || []).map((city) => Number(city.id)));
    const [fillInNeeded, setFillInNeeded] = useState(Boolean(Number(topicData?.['fill_in_needed'] || 0)));
    const [eventDateUnix, setEventDateUnix] = useState(topicData?.['event_date'] ? moment.utc(topicData['event_date']).unix() : moment().add(1, 'day').unix());
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [isPostingTopic, setPostingTopic] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [images, setImages] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    // endregion

    // region Derived Data
    const isEditing = !!topicData;
    // endregion

    // region Function - Go Create Topic
    const imageCompressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
    const goPostTopic = () => {
        if (isPostingTopic) return;

        if (!title.trim() || !description.trim()) {
            showAlert({ text: 'Title and topic content are required' })
            return;
        }

        if (postingCities.length === 0) {
            showAlert({ text: 'Please select the cities where you want to post this topic to' })
            return;
        }

        showAlert({
            text: `Confirm ${isEditing ? 'editing' : 'posting'} this topic to the Truckster Vendor Chat?`,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    setPostingTopic(true);
                    setUploadProgress(0);

                    // Compress images
                    Promise
                        .all(imageFiles.map((photo) => imageCompression(photo, imageCompressionOptions)))
                        .then((compressedImages) => {
                            const apiCall = isEditing ? updateTopic : createNewTopic;
                            apiCall({
                                title: title,
                                description: description,
                                imageList: compressedImages,
                                servingCityIDs: postingCities,
                                fillInNeeded: fillInNeeded,
                                eventDateStr: moment.unix(eventDateUnix).utc().format('YYYY-MM-DD HH:mm:ss'),
                                onProgress: setUploadProgress
                            })
                                .then((result) => {
                                    // Because the server will upload images to the local storage of it first
                                    // so the initial returned links are "local server" links
                                    // which will quickly become invalid as soon as they are uploaded to S3
                                    // Solution:
                                    // 1. After upload is finished, extract the newly uploaded elements from the response.
                                    // 2. Replace each of them with the device local link accordingly (in the tempImageList array).
                                    const uploadedImages = result.images.slice(Math.max(result.length - images.length, 0));
                                    const replacedImages = uploadedImages.map((item, index) => Object.assign(cloneDeep(item), { path: images[index].path }));
                                    const newTopicResult = Object.assign(cloneDeep(result), { images: replacedImages });

                                    // Callback
                                    onNewTopicCreated(newTopicResult, postingCities);

                                    // Close screen
                                    RootNavigation.goBack();
                                })
                                .catch((error) => showAppError(error))
                                .finally(() => setPostingTopic(false));
                        })
                }
            }
        })
    }
    // endregion

    // region Render Main Content
    const renderMainContent = () => (
        <View style={{ alignSelf: 'stretch' }}>
            {/* Title */}
            <TextBox
                ref={txtTitle}
                title={'Topic Title'}
                placeholder={'Enter a title for your topic'}
                autoCapitalize={'words'}
                keyboardType={'default'}
                value={title}
                onChangeText={setTitle}
                onSubmitEditing={() => txtDescription.current?.focus()}
            />

            {/* Description */}
            <TextBox
                ref={txtDescription}
                title={'Topic Content'}
                placeholder={'What do you want to talk about?'}
                multiline={true}
                style={{ marginTop: 24 }}
                contentBoxStyle={{ height: 200 }}
                value={description?.toString()}
                onChangeText={setDescription}
            />
        </View>
    )
    // endregion

    // region Render Image View
    const renderImageItem = (item, index) => (
        <View
            key={item.id || item['uniqueName']}
            style={{ width: PHOTO_SIZE, height: PHOTO_SIZE, backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, marginRight: 8 }}>
            {/* Photo */}
            <TouchableOpacity
                onPress={() => {
                    setPhotoCarouselOpened(true);
                    setCurrentPhotoIndex(index);
                }}>
                <Image
                    source={{ uri: item.uri || item['path_webp'] || item.path || item }}
                    style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }}
                    resizeMode={'cover'}
                />
            </TouchableOpacity>

            {/* Remove Button */}
            {
                !isPostingTopic &&
                <TouchableOpacity
                    onPress={() => {
                        const newImageList = cloneDeep(images).filter((imageItem) => imageItem.uniqueName !== item.uniqueName);
                        const newImageFileList = cloneDeep(imageFiles).filter((imageFile, imageIndex) => Number(imageIndex) !== Number(index));
                        setImages(newImageList);
                        setImageFiles(newImageFileList);
                    }}
                    style={[
                        { position: 'absolute', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                        { top: 0, right: 0, width: 40, height: 40, zIndex: 5, borderBottomLeftRadius: 4 }
                    ]}>
                    <Icon
                        name={'x'}
                        color={'white'}
                        size={22}
                    />
                </TouchableOpacity>
            }
        </View>
    )

    const renderImageView = () => (
        <View style={{ alignSelf: 'stretch', marginTop: 40 }}>
            <Text style={{ fontWeight: '500', marginBottom: 0 }}>
                Images
            </Text>

            <View style={{ width: '100%', flexDirection: 'row', alignItems: 'flex-end', marginTop: 16 }}>
                {/* New Image Button */}
                {
                    (images.length < MAX_PHOTO_COUNT) &&
                    <TouchableOpacity
                        onPress={() => photoPicker.current?.click()}
                        style={{ backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, height: PHOTO_SIZE, width: PHOTO_SIZE, marginRight: 8, alignItems: 'center', justifyContent: 'center' }}>
                        <Image
                            source={require('../../images/iconLineAddPhoto@3x.png')}
                            style={{ width: 32, height: 32 }}
                            tintColor={GLOBAL.COLOR.BASIC.BLACK_TWO}
                        />
                    </TouchableOpacity>
                }

                {/* Image List */}
                {images.map((item, index) => renderImageItem(item, index))}

                {/* Photo Picker */}
                <input
                    id={'photoPicker'}
                    type={'file'}
                    accept={'.png, .jpg, .jpeg'}
                    multiple={true}
                    ref={photoPicker}
                    style={{ display: 'none' }}
                    onChange={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        const fileList = Object.values(event.target.files || {});
                        if (fileList.length > MAX_PHOTO_COUNT) {
                            showAlert({ text: `Please select ${MAX_PHOTO_COUNT - images.length} photos or fewer` });
                            return;
                        }
                        setImageFiles(imageFiles.concat(fileList));
                        setImages(images.concat(fileList.map((file) => {
                            const path = URL.createObjectURL(file);
                            const name = getFileName(path);
                            return({
                                path,
                                name,
                                uniqueName: `${moment().unix()}_${name}`,
                                isNew: true
                            })
                        })));
                    }}
                />
            </View>
        </View>
    )
    // endregion

    // region Render Posting Cities View
    console.log('postingCities', postingCities);
    const renderPostingCitiesView = () => (
        <View style={{ alignSelf: 'stretch', marginTop: 56 }}>
            <Text style={{ fontWeight: '500', marginBottom: 8 }}>
                Posting Cities
            </Text>

            <TextBox
                title={'Cities'}
                onPress={() => cityPicker.current?.showModal()}
                value={
                    postingCities.length === 0 ?
                        'Tap to select'
                        :
                        (
                            (postingCities.length === Object.keys(GLOBAL.CITIES).length) ?
                                'All Cities'
                                :
                                postingCities.map((cityID) => GLOBAL.CITIES[cityID].name).join(', ')
                        )
                }
            />
        </View>
    )
    // endregion

    // region Render Start Time Picker
    const renderEventDatePicker = () => (
        <>
            <DatePickerModal
                locale={'en'}
                mode={'single'}
                visible={showDatePicker}
                date={eventDateUnix ? moment.unix(eventDateUnix).toDate() : moment().toDate()}
                saveLabelDisabled={true}
                validRange={{ startDate: moment().startOf('day').toDate() }}
                onChange={({ date }) => {
                    const newDate = moment.unix(eventDateUnix).clone();
                    newDate.set('date', date.getDate());
                    newDate.set('month', date.getMonth());
                    newDate.set('year', date.getFullYear());
                    setEventDateUnix(newDate.unix());
                    setShowDatePicker(false);
                    setShowTimePicker(true);
                }}
                onDismiss={() => setShowDatePicker(false)}
            />

            <TimePickerModal
                locale={'en'}
                mode={'single'}
                defaultInputType={'keyboard'}
                visible={showTimePicker}
                hours={moment.unix(eventDateUnix).hours()}
                minutes={moment.unix(eventDateUnix).minutes()}
                use24HourClock={false}
                onConfirm={({ hours, minutes }) => {
                    const newDate = moment.unix(eventDateUnix).clone();
                    newDate.set('minutes', minutes);
                    newDate.set('hours', hours);
                    setEventDateUnix(newDate.unix());
                    setShowTimePicker(false);
                }}
                onDismiss={() => setShowTimePicker(false)}
            />
        </>
    );
    // endregion

    // region Render Fill In Needed View
    const renderFillInNeededView = () => (
        <View style={{ alignSelf: 'stretch', marginTop: 56 }}>
            <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch' }}>
                {/* Title */}
                <Text style={{ fontWeight: '500' }}>
                    Fill in Needed
                </Text>

                {/* Blank Space */}
                <View style={{ flex: 1 }} />

                {/* Switch */}
                <Switch
                    value={fillInNeeded}
                    trackColor={'#DEDEDE'}
                    activeTrackColor={GLOBAL.COLOR.BASIC.UI_GREEN}
                    activeThumbColor={'white'}
                    thumbColor={'white'}
                    onValueChange={setFillInNeeded}
                />
            </View>

            {/* Event Date */}
            {
                fillInNeeded &&
                <TouchableOpacity
                    onPress={() => setShowDatePicker(true)}
                    style={{ paddingTop: 8, flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', marginTop: 16 }}>
                    <Text style={{ color: GLOBAL.COLOR.BASIC.UI_BLUE, fontSize: 18 }}>
                        Event Date:{' '}
                    </Text>

                    <Text style={{ fontWeight: 'bold', color: GLOBAL.COLOR.BASIC.UI_BLUE, fontSize: 18, marginRight: 8 }}>
                        {moment.unix(eventDateUnix).format('h:mm A - MMM DD, YYYY')}
                    </Text>

                    <Icon
                        name={'edit'}
                        color={GLOBAL.COLOR.BASIC.UI_BLUE}
                        size={20}
                    />
                </TouchableOpacity>
            }
        </View>
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}>
            {/* Nav Bar */}
            <NavBar
                title={`${isEditing ? 'Update' : 'New'} Topic`}
                leftButtonIconName={'close'}
                rightButtonTitle={isEditing ? 'Save Topic' :'Create Topic'}
                rightButtonOnPress={goPostTopic}
            />

            {/* Content */}
            <ScrollView
                style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingVertical: 24, paddingHorizontal: '15%' }}>
                <View style={{ alignSelf: 'stretch', padding: 32, backgroundColor: 'white', borderColor: GLOBAL.COLOR.DIVIDER, borderWidth: 1, overflow: 'hidden' }}>
                    {renderMainContent()}
                    {!isEditing && renderImageView()}
                    {renderPostingCitiesView()}
                    {renderFillInNeededView()}
                    {renderEventDatePicker()}
                </View>
            </ScrollView>

            {/* City Picker Modal */}
            <CityPickerModal
                ref={cityPicker}
                title={'Select Posting Cities'}
                showDescription={true}
                showSelectedCount={true}
                useCheckBoxes={true}
                visibleCityIDs={(globalTruckData?.city || []).map((city) => Number(city.id))}
                selectedCityIDs={postingCities}
                onCitySelected={setPostingCities}
                rightButtonText={'Apply'}
            />

            {/* Light Box */}
            <Lightbox
                plugins={[Captions]}
                open={photoCarouselOpened}
                close={() => setPhotoCarouselOpened(false)}
                index={currentPhotoIndex}
                on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                slides={images.map((photo, index) => ({
                    key: `imageItem_${photo.uniqueName || photo.id || index}`,
                    src: photo['path_webp'] || photo['path'],
                    id: photo.uniqueName || photo.id || index,
                    title: `Photo ${index + 1} of ${images.length}`
                }))}
                carousel={{ imageFit: 'contain' }}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
            />

            {/* Loading Cover */}
            {
                isPostingTopic &&
                <LoadingView
                    isAbsolute={true}
                    description={`${isEditing ? 'Updating' : 'Posting'} your topic... ${uploadProgress}%`}
                />
            }
        </View>
    );
    // endregion
}
